package com.arena_web.data.http

import com.arena_web.core.config.AppConfig
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.request.parameter
import io.ktor.client.statement.*
import io.ktor.http.*

/**
 * Safely parse response body as [ApiResponse].
 * If the body is empty (e.g. 201 Created, 204 No Content with no body),
 * returns a synthetic success [ApiResponse] instead of crashing.
 *
 * For auth/permission failures (401/403) we return a clean user-facing error message
 * instead of the raw Ktor "Expected response body of the type ApiResponse but was
 * SourceByteReadChannel" exception (which happens when the backend returns an empty
 * unauthorized body that can't be deserialized).
 */
suspend inline fun <reified T> HttpResponse.safeBody(): ApiResponse<T> {
    if (status == HttpStatusCode.Unauthorized) {
        return ApiResponse(
            code = status.value,
            data = null,
            errors = listOf("Session expired. Please log in again."),
        )
    }
    if (status == HttpStatusCode.Forbidden) {
        return ApiResponse(
            code = status.value,
            data = null,
            errors = listOf("You don't have permission to perform this action."),
        )
    }
    return try {
        body()
    } catch (e: Exception) {
        ApiResponse(
            code = status.value,
            data = null,
            errors = if (status.isSuccess()) null else listOf(e.message ?: "Request failed: $status")
        )
    }
}

suspend inline fun <reified T> HttpClient.apiGet(path: String): ApiResponse<T> {
    return get(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
    }.safeBody()
}

suspend inline fun <reified T> HttpClient.apiGet(
    path: String,
    params: Map<String, String>
): ApiResponse<T> {
    return get(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        params.forEach { (key, value) -> parameter(key, value) }
    }.safeBody()
}

suspend inline fun <reified T, reified R> HttpClient.apiPost(
    path: String,
    body: R
): ApiResponse<T> {
    return post(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        setBody(body)
    }.safeBody()
}

suspend inline fun <reified T> HttpClient.apiPost(path: String): ApiResponse<T> {
    return post(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
    }.safeBody()
}

suspend fun HttpClient.apiPostNoBody(path: String): HttpStatusCode {
    return post(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
    }.status
}

suspend inline fun <reified T, reified R> HttpClient.apiPut(
    path: String,
    body: R
): ApiResponse<T> {
    return put(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        setBody(body)
    }.safeBody()
}

suspend inline fun <reified T, reified R> HttpClient.apiPatch(
    path: String,
    body: R
): ApiResponse<T> {
    return patch(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        setBody(body)
    }.safeBody()
}

suspend inline fun <reified T> HttpClient.apiDelete(path: String): ApiResponse<T> {
    return delete(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
    }.safeBody()
}

suspend fun HttpClient.apiDeleteNoBody(path: String): HttpStatusCode {
    return delete(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
    }.status
}

suspend inline fun <reified T, reified R> HttpClient.apiDelete(
    path: String,
    body: R
): ApiResponse<T> {
    return delete(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        setBody(body)
    }.safeBody()
}

suspend inline fun <reified T> HttpClient.apiPostMultipart(
    path: String,
    fileBytes: ByteArray,
    fileName: String,
    fileContentType: String,
    formFields: Map<String, String> = emptyMap()
): ApiResponse<T> {
    return submitFormWithBinaryData(
        url = AppConfig.getApiUrl(path),
        formData = formData {
            append("file", fileBytes, Headers.build {
                append(HttpHeaders.ContentType, fileContentType)
                append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
            })
            formFields.forEach { (key, value) ->
                append(key, value)
            }
        }
    ) {
        // Remove default Content-Type so multipart boundary is set by Ktor
        headers.remove(HttpHeaders.ContentType)
        // Ktor JS engine doesn't always propagate `defaultRequest` headers to
        // `submitFormWithBinaryData`, so we re-add Authorization and X-Platform
        // explicitly to guarantee multipart uploads are authenticated and tagged.
        headers.remove(PLATFORM_HEADER)
        headers.append(PLATFORM_HEADER, PLATFORM_VALUE)
        Storage.get(StorageKeys.ACCESS_TOKEN)?.let { token ->
            headers.remove(HttpHeaders.Authorization)
            headers.append(HttpHeaders.Authorization, "Bearer $token")
        }
    }.safeBody()
}

suspend inline fun <reified T> HttpClient.apiPostMultipartFields(
    path: String,
    formFields: Map<String, String>
): ApiResponse<T> {
    return submitFormWithBinaryData(
        url = AppConfig.getApiUrl(path),
        formData = formData {
            formFields.forEach { (key, value) ->
                append(key, value)
            }
        }
    ) {
        headers.remove(HttpHeaders.ContentType)
        headers.remove(PLATFORM_HEADER)
        headers.append(PLATFORM_HEADER, PLATFORM_VALUE)
        Storage.get(StorageKeys.ACCESS_TOKEN)?.let { token ->
            headers.remove(HttpHeaders.Authorization)
            headers.append(HttpHeaders.Authorization, "Bearer $token")
        }
    }.safeBody()
}
